import React, { useState, useEffect } from 'react';
import { Button, Form, PopConfirm, Table } from '@maxtropy/components';
import { Col, Row, FormInstance } from 'antd';
import EnvDeviceChoose from './EnvDeviceChoose';
import DataPointConfigModal, { DataPointItem } from './DataPointConfigModal';
import styles from '../index.module.scss';

export interface EnvDeviceItem {
  id: string; // 唯一标识
  deviceId: number;
  deviceName: string;
  dataPoints?: DataPointItem[];
}

interface EnvConfigProps {
  isEdit: boolean;
  form: FormInstance<any>;
}

const EnvConfig: React.FC<EnvConfigProps> = ({ isEdit, form }) => {
  const [envDeviceList, setEnvDeviceList] = useState<EnvDeviceItem[]>([]);
  const [dataPointModalOpen, setDataPointModalOpen] = useState(false);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState<number>();

  const watchedEnvList = Form.useWatch('envDeviceList', form);

  useEffect(() => {
    if (watchedEnvList) {
      setEnvDeviceList(watchedEnvList);
    }
  }, [watchedEnvList]);

  const handleDeviceSelect = (devices: { deviceId: number; deviceName: string }[]) => {
    const currentList = form.getFieldValue('envDeviceList') || [];
    const existingIds = currentList.map((item: EnvDeviceItem) => item.deviceId);

    // 只添加不存在的设备，避免重复
    const newItems: EnvDeviceItem[] = devices
      .filter(device => !existingIds.includes(device.deviceId))
      .map(device => ({
        id: `${Date.now()}-${device.deviceId}`,
        deviceId: device.deviceId,
        deviceName: device.deviceName,
        dataPoints: [],
      }));

    if (newItems.length === 0) return; // 没有新设备需要添加

    const newList = [...currentList, ...newItems];
    form.setFieldValue('envDeviceList', newList);
    setEnvDeviceList(newList);
  };

  const handleDeleteDevice = (index: number) => {
    const currentList = form.getFieldValue('envDeviceList') || [];
    const newList = currentList.filter((_: any, i: number) => i !== index);
    form.setFieldValue('envDeviceList', newList);
    setEnvDeviceList(newList);
  };

  const handleOpenDataPointConfig = (index: number) => {
    setCurrentDeviceIndex(index);
    setDataPointModalOpen(true);
  };

  const handleDataPointChange = (dataPoints: DataPointItem[]) => {
    if (currentDeviceIndex === undefined) return;
    const currentList = form.getFieldValue('envDeviceList') || [];
    currentList[currentDeviceIndex].dataPoints = dataPoints;
    form.setFieldValue('envDeviceList', currentList);
    setEnvDeviceList(currentList);
  };

  const existingDeviceIds = envDeviceList.map(item => item.deviceId);

  const columns = [
    {
      title: '设备名称',
      dataIndex: 'deviceName',
      width: 300,
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 200,
      render: (_: any, record: EnvDeviceItem, index: number) => {
        return (
          <Row gutter={8}>
            <Col>
              <Button type="link" disabled={!isEdit} onClick={() => handleOpenDataPointConfig(index)}>
                数据点展示配置
              </Button>
            </Col>
            <Col>
              <PopConfirm title="确定删除该设备吗？" onConfirm={() => handleDeleteDevice(index)}>
                <Button disabled={!isEdit} type="link">
                  删除
                </Button>
              </PopConfirm>
            </Col>
          </Row>
        );
      },
    },
  ];

  const currentDevice = currentDeviceIndex !== undefined ? envDeviceList[currentDeviceIndex] : undefined;

  return (
    <Row gutter={8}>
      <Col span={24}>
        <Row gutter={8} style={{ marginBottom: 8 }}>
          <Col>
            <span style={{ lineHeight: '32px' }}>环保检测设备：</span>
          </Col>
          <Col>
            <EnvDeviceChoose
              disabled={!isEdit}
              value={envDeviceList.map(item => ({
                deviceId: item.deviceId,
                deviceName: item.deviceName,
              }))}
              onChange={handleDeviceSelect}
              existingDeviceIds={existingDeviceIds}
            />
          </Col>
        </Row>
        <Form.Item noStyle name="envDeviceList">
          <Table rowKey="id" columns={columns} dataSource={envDeviceList} pagination={false} scroll={{ x: 500 }} />
        </Form.Item>
      </Col>
      {dataPointModalOpen && currentDevice && (
        <DataPointConfigModal
          open={dataPointModalOpen}
          setOpen={setDataPointModalOpen}
          deviceId={currentDevice.deviceId}
          value={currentDevice.dataPoints}
          onChange={handleDataPointChange}
        />
      )}
    </Row>
  );
};

export default EnvConfig;
